/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { Parameter } from "src/utils/parameters/types";
import { parseQueryString } from "src/utils/links/query";
import { isStringArray } from "src/utils/array";
import qs from "qs";
import md5 from "md5";
import { fromPairs } from "lodash";
import type { ParsedUrlQueryInput } from "querystring";

export const paramQueryStringKey = (p: Parameter) =>
  `${p.key}_${p.type[0]}${hash(
    `${p.type[0]}${p.query_id}${p.default_value}${p.default_values ?? ""}${
      p.isFreeformAllowed ? "'freeform'" : ""
    }`
  )}`;

export function parameterQueryParams(
  parameters: Parameter[]
): ParsedUrlQueryInput {
  return fromPairs(
    parameters.map((p): [string, ParsedUrlQueryInput[string]] => {
      const paramId = paramQueryStringKey(p);
      if (!p.isMultiselect) {
        if (p.value !== p.default_value) {
          return [paramId, p.value];
        } else {
          return [paramId, undefined];
        }
      } else {
        if (p.values?.join("\n") !== p.default_values?.join("\n")) {
          return [paramId, p.values];
        } else {
          return [paramId, []];
        }
      }
    })
  );
}

// Format a list of parameters as a query string.
export const formatParametersQuery = (parameters: Parameter[]) => {
  return qs.stringify(parameterQueryParams(parameters));
};

// Parse parameter key/value pairs from a query string.
// Keep the keys that also exist in a list of defaults.
// Reject values that already have the default value.
// Reject enum values that don't exist in enumOptions.
// Reject numbers that can't be parsed.
export const parseParametersQuery = (
  defaults: Parameter[],
  query: string
): Parameter[] => {
  const parsed = parseQueryString(query);
  const parameters: Parameter[] = [];

  defaults.forEach((d) => {
    const paramHash = hash(
      `${d.type[0]}${d.query_id}${d.default_value}${d.default_values ?? ""}${
        d.isFreeformAllowed ? "'freeform'" : ""
      }`
    );
    const uniqueKey = `${d.key}_${d.type[0]}${paramHash}`;

    function getParsedValues(): string[] {
      const parsedValue = parsed[uniqueKey];

      if (typeof parsedValue === "string") {
        return [parsedValue];
      }
      if (isStringArray(parsedValue)) {
        return parsedValue;
      }
      return [];
    }

    function isValidEnumOption(value: string) {
      return d.isFreeformAllowed === true || d.enumFromResults
        ? true
        : d.enumOptions?.includes(value);
    }

    if (!d.isMultiselect) {
      // Prefer specific param (wtih type and hash) from url, use param by just key as fallback.
      const value = parsed[uniqueKey] ?? parsed[d.key];
      if (
        typeof value === "string" &&
        value !== "" &&
        value !== d.default_value &&
        (d.type !== "enum" || isValidEnumOption(value)) &&
        (d.type !== "number" || Number.isFinite(parseFloat(value)))
      ) {
        parameters.push({ ...d, value });
      }
    } else {
      const values = getParsedValues().filter(isValidEnumOption);

      if (
        values.length > 0 &&
        values.join("\n") !== d.default_values?.join("\n")
      ) {
        parameters.push({
          ...d,
          values,
        });
      }
    }
  });

  return parameters;
};

export const hash = (query: string): string => {
  return md5(query).slice(0, 5);
};
